import { A, useParams } from '@solidjs/router';
import type { JSX } from 'solid-js';
import { Show } from 'solid-js';
import { CardGridSkeleton } from '../components/CardGridSkeleton';
import { DevopsReportView } from '../components/DevopsReportView';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { QueryBoundary } from '../components/QueryBoundary';
import { buttonClass } from '../components/ui/Button';
import { t } from '../i18n';
import { ApiError } from '../lib/api';
import { orgHref } from '../lib/org';
import { SESSION_TOKEN } from '../lib/session';
import { useTaskReport } from '../lib/task-report';

// What a run DELIVERED (arc 101 slice 06) — `/fleet/tasks/:task_id/report` over
// `GET /v1/tasks/:id/report`.
//
// CONTAINER: it resolves the route param, owns the one read, and hands the view
// object to `<DevopsReportView/>`. The report is addressed by TASK, not by run,
// and that is the projection's own rule rather than a filing choice — the latest
// run wins for the report while filings and blockers span every attempt, so a
// per-run address could not answer the question this page asks.
//
// A SIBLING OF `/fleet/runs/:run_id`, NOT A REPLACEMENT. That view is the live
// timeline of what a run DID; this is what it left behind. Both are linked from
// here so a reader who wants the events is one click away.
//
// `token` is injectable for tests (the house seam every route takes); the router
// mounts this with no props → the dev session.
export function DevopsReport(props: { token?: string | null } = {}): JSX.Element {
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const params = useParams<{ task_id: string }>();
  const report = useTaskReport(() => params.task_id, token);
  // A 404 is an ANSWER — this account has no such task (`task-report.ts`) — so
  // it renders as its own line rather than the boundary's error box, whose Retry
  // would 404 for ever. Same rule as `TaskDetailDrawer` and `EpicView`.
  const gone = (): boolean =>
    report.isError && report.error instanceof ApiError && report.error.status === 404;

  return (
    <>
      <PageHeader
        title={t('devops_report.title')}
        desc={t('devops_report.subtitle')}
        actions={
          <>
            <Show when={report.data?.run_id}>
              {(runId) => (
                <A
                  class={buttonClass({ variant: 'outline' })}
                  href={orgHref(`/fleet/runs/${runId()}`)}
                >
                  {t('devops_report.view_run')}
                </A>
              )}
            </Show>
            {/* Deep route with no rail entry (#298) — keep an explicit way back. */}
            <A class={buttonClass({ variant: 'outline' })} href={orgHref('/fleet')}>
              {t('devops_report.back')}
            </A>
          </>
        }
      />

      {/* No `isEmpty`: the body is one OBJECT and always present. "Delivered
          nothing yet" is `report: null` INSIDE it — a 200 the view names in its
          own words, never the boundary's generic empty box. */}
      <Show when={!gone()} fallback={<EmptyState message={t('devops_report.not_found')} />}>
        <QueryBoundary
          query={report}
          loading={<CardGridSkeleton listClass="metric-grid" count={2} />}
        >
          {(view) => <DevopsReportView view={view} />}
        </QueryBoundary>
      </Show>
    </>
  );
}
